/**
 * ChurchRegionPage
 * ================
 * Region-level rollup for any church node. Picks up the church_id from the
 * URL and renders:
 *   - The root church (name, level, city)
 *   - 3-up KPI tiles (churches under, home groups, members)
 *   - Active pledges block (count + by-currency balance)
 *   - 30-day giving + offerings totals
 *   - List of descendant churches with their level + member count
 *   - List of home groups (broken out so a District Overseer can drill in)
 */
import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Network, Users, Home, HandCoins, Gift, Coins } from 'lucide-react';

const apiBase = process.env.REACT_APP_BACKEND_URL || '';
const tokenOf = () => localStorage.getItem('admin_token') || localStorage.getItem('eventflow_token') || localStorage.getItem('token') || '';
const fetchJson = async (url) => {
  const r = await fetch(`${apiBase}${url}`, { headers: { Authorization: `Bearer ${tokenOf()}` } });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};
const fmt = (n, c = 'USD') => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const LevelBadge = ({ level }) => {
  const map = {
    hq: 'bg-rose-100 text-rose-700 border-rose-200',
    province: 'bg-purple-100 text-purple-700 border-purple-200',
    region: 'bg-purple-100 text-purple-700 border-purple-200',
    district: 'bg-sky-100 text-sky-700 border-sky-200',
    assembly: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    local: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    ministry: 'bg-amber-100 text-amber-700 border-amber-200',
    home_group: 'bg-orange-100 text-orange-700 border-orange-200',
    cell: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  const cls = map[(level || '').toLowerCase()] || 'bg-slate-100 text-slate-700';
  return <Badge variant="outline" className={`text-[10px] capitalize ${cls}`}>{level || '—'}</Badge>;
};

export default function ChurchRegionPage() {
  const { churchId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['church-region', churchId],
    queryFn: () => fetchJson(`/api/church/region/by-church/${churchId}`),
    enabled: !!churchId,
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (error) return <div className="p-6 text-rose-600">Failed to load region — {error.message}</div>;

  const root = data?.root || {};
  const m = data?.metrics || {};
  const churches = data?.descendants || [];
  const homeGroups = data?.home_groups || [];
  const pledges = m.active_pledges || { count: 0, by_currency: {} };
  const giving = m.giving_30d || { count: 0, by_currency: {} };
  const offerings = m.offerings_30d || { count: 0, by_currency: {} };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="church-region-page">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1">
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Network className="h-6 w-6 text-emerald-700" />
              {root.name || 'Region'}
            </h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
              <LevelBadge level={root.level} />
              {root.city && <span>· {root.city}</span>}
              {root.country && <span>· {root.country}</span>}
            </div>
          </div>
          <Link to={`/admin/churches/${root.id}`}>
            <Button variant="outline" size="sm">Open root church</Button>
          </Link>
        </div>

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile icon={<Network className="h-5 w-5" />} label="Churches under" value={m.church_count || 0} tone="emerald" testid="region-kpi-churches" />
          <Tile icon={<Home className="h-5 w-5" />} label="Home groups" value={m.home_group_count || 0} tone="amber" testid="region-kpi-home-groups" />
          <Tile icon={<Users className="h-5 w-5" />} label="Members" value={m.member_count || 0} tone="sky" testid="region-kpi-members" />
          <Tile icon={<HandCoins className="h-5 w-5" />} label="Active pledges" value={pledges.count || 0} tone="rose" testid="region-kpi-pledges" />
        </div>

        {/* Pledges */}
        <Card data-testid="region-pledges-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><HandCoins className="h-4 w-4 text-rose-600" /> Active pledges</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(pledges.by_currency || {}).length === 0 ? (
              <p className="text-sm text-slate-400">No active pledges in this region.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {Object.entries(pledges.by_currency).map(([c, v]) => (
                  <div key={c} className="px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 min-w-0 sm:min-w-[180px]">
                    <p className="text-xs uppercase tracking-wide text-rose-700">{c}</p>
                    <p className="text-lg font-bold">{fmt(v.pledged, c)}</p>
                    <p className="text-xs text-rose-600 mt-0.5">Balance: {fmt(v.balance, c)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Giving + Offerings */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card data-testid="region-giving-card">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Gift className="h-4 w-4 text-emerald-700" /> Giving — last 30 days</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-2">{giving.count || 0} record{giving.count === 1 ? '' : 's'}</p>
              {Object.keys(giving.by_currency || {}).length === 0 ? (
                <p className="text-sm text-slate-400">No giving recorded in this region in the last 30 days.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(giving.by_currency).map(([c, total]) => (
                    <div key={c} className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-700">{c}</p>
                      <p className="text-base font-bold">{fmt(total, c)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card data-testid="region-offerings-card">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Coins className="h-4 w-4 text-amber-700" /> Offerings — last 30 days</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-2">{offerings.count || 0} record{offerings.count === 1 ? '' : 's'}</p>
              {Object.keys(offerings.by_currency || {}).length === 0 ? (
                <p className="text-sm text-slate-400">No offerings recorded in this region in the last 30 days.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(offerings.by_currency).map(([c, total]) => (
                    <div key={c} className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                      <p className="text-[10px] uppercase tracking-wide text-amber-700">{c}</p>
                      <p className="text-base font-bold">{fmt(total, c)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Descendant churches */}
        <Card data-testid="region-churches-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Churches in region ({churches.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {churches.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-slate-400">This church has no descendants yet — assign other churches' parent to this one to build the region.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Level</th>
                  <th className="text-left py-2 px-3">Location</th>
                  <th className="text-right py-2 px-3">Members</th>
                  <th className="text-right py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {churches.map(c => (
                    <tr key={c.id} className="border-b last:border-0" data-testid={`region-church-${c.id}`}>
                      <td className="py-2 px-3 font-medium text-slate-700">{c.name}</td>
                      <td className="py-2 px-3"><LevelBadge level={c.level} /></td>
                      <td className="py-2 px-3 text-xs text-slate-500">{[c.city, c.country].filter(Boolean).join(', ') || '—'}</td>
                      <td className="py-2 px-3 text-right text-xs text-slate-600">{c.member_count || 0}</td>
                      <td className="py-2 px-3 text-right">
                        <Link to={`/admin/churches/${c.id}`}>
                          <Button size="sm" variant="ghost" className="h-7 text-xs">Open</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Home groups */}
        {homeGroups.length > 0 && (
          <Card data-testid="region-home-groups-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Home className="h-4 w-4 text-orange-600" />Home groups ({homeGroups.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {homeGroups.map(hg => (
                  <Link key={hg.id} to={`/admin/churches/${hg.id}`} className="px-3 py-2 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-900 text-sm flex items-center justify-between gap-2">
                    <span className="truncate">{hg.name}</span>
                    <span className="text-[10px] text-orange-600">{hg.member_count || 0} member{hg.member_count === 1 ? '' : 's'}</span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

const TONES = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  sky: 'bg-sky-50 border-sky-200 text-sky-900',
  rose: 'bg-rose-50 border-rose-200 text-rose-900',
};
function Tile({ icon, label, value, tone, testid }) {
  return (
    <div className={`p-4 rounded-xl border ${TONES[tone] || TONES.emerald}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="opacity-70">{icon}</div>
      </div>
    </div>
  );
}
